// @ts-nocheck
import { Form } from "@shared/antd/form";
import { Select } from "@shared/antd/select";
import React, { createElement } from "react";
import { observer } from "mobx-react";
import { DataCollectionStore, FormField, injectMainStore, MainStoreInjected, Msg, WithId } from "@cuba-platform/react";
import { FormComponentProps, FormItemProps } from "antd/lib/form";
import { GetFieldDecoratorOptions } from "antd/lib/form/Form";
import { MetaPropertyInfo } from "@cuba-platform/rest/dist-node/model";
import { DEFAULT_DATE_PATTERN } from "../../util/Date/Date";
import { MultiFileUpload } from "../MultiFileUpload";

type ReadonlyFieldProps = MainStoreInjected &
  FormComponentProps & {
  entityName: string;
  propertyName: string;
  optionsContainer?: Pick<DataCollectionStore<WithId>, "items" | "status">;
  formItemKey?: string;
  formItemOpts?: FormItemProps;
  fieldDecoratorId?: string;
  getFieldDecoratorOpts?: GetFieldDecoratorOptions;
  disabled?: boolean;
  extraProps?: any;
  style?: React.CSSProperties;
  format?: string | string[];
  customLabel?: JSX.Element;
  isManyFiles?: boolean;
  component?: React.ComponentType<any>;
};

export const NewReadonlyField: React.FC<ReadonlyFieldProps> = injectMainStore(
  observer((props) => {
    const {
      form,
      entityName,
      propertyName,
      optionsContainer,
      fieldDecoratorId,
      getFieldDecoratorOpts,
      formItemKey,
      disabled,
      extraProps,
      style,
      isManyFiles,
      format,
      customLabel,
      component,
      mainStore,
    } = props;
    const decoratorId = fieldDecoratorId || propertyName;

    const { getFieldDecorator } = form;

    const formItemOpts = { ...props.formItemOpts };

    if (!formItemOpts.label) {
      formItemOpts.label =
        customLabel ||
        createElement(Msg, {
          entityName,
          propertyName,
        });
    }

    const propertyInfo = getPropertyInfo(entityName, propertyName, mainStore);
    const isDate =
      propertyInfo &&
      (propertyInfo.type === "date" || propertyInfo.type === "dateTime");
    const isFile =
      propertyInfo && propertyInfo.type === "sys$FileDescriptor";
    const isToManyRelation =
      (propertyInfo && isToManyRelationFn(propertyInfo.cardinality)) ||
      isManyFiles;
    const isAssociation =
      propertyInfo && propertyInfo.attributeType === "ASSOCIATION";
    const isSelectField = !isFile && isAssociation && optionsContainer;

    const resolvedFormat =
      format || (isDate ? DEFAULT_DATE_PATTERN : undefined);

    const commonProps = {
      entityName,
      propertyName,
      disabled,
      optionsContainer,
      style,
      format: resolvedFormat,
      ...extraProps,
    };

    const mode = propertyInfo && getSelectMode(propertyInfo.cardinality);

    let fieldComponent: React.ReactNode;

    if (component) {
      fieldComponent = createElement(component, commonProps);
    } else if (isSelectField) {
      fieldComponent = createElement(EntitySelectField, {
        mode,
        optionsContainer,
        allowClear: getAllowClear(propertyInfo),
        ...commonProps,
      });
    } else if (isFile && isToManyRelation) {
      fieldComponent = createElement(MultiFileUpload, commonProps);
    } else {
      fieldComponent = createElement(FormField, commonProps);
    }

    // IMPORTANT: Form.Item key и label используют оригинальное propertyName (чтобы не ломать локали/metadata)
    // getFieldDecorator будет зарегистрирован под decoratorId (fieldDecoratorId if provided)
    return createElement(
      Form.Item,
      { key: formItemKey || propertyName, ...formItemOpts },
      getFieldDecorator(decoratorId, getFieldDecoratorOpts)(fieldComponent),
    );
  }),
);


const getAllowClear = (propertyInfo: any) => !propertyInfo.mandatory;

const getSelectMode = (cardinality: string) => {
  if (cardinality === "ONE_TO_MANY" || cardinality === "MANY_TO_MANY") {
    return "multiple";
  }
  return "default";
};

const getPropertyInfo = (
  entityName: string,
  propertyName: string,
  mainStore: any,
): MetaPropertyInfo | null => {
  const metaClass = mainStore!.metadata!.find(
    (mci) => mci.entityName === entityName,
  );
  if (!metaClass) {
    return null;
  }
  return metaClass.properties.find((prop) => prop.name === propertyName) || null;
};

const isToManyRelationFn = (cardinality: string) =>
  cardinality === "ONE_TO_MANY" || cardinality === "MANY_TO_MANY";

const EntitySelectField = observer((props) => {
  const { optionsContainer, ...rest } = props;

  return createElement(
    Select,
    {
      ...rest,
      loading: optionsContainer?.status === "LOADING",
      optionFilterProp: "children",
      showSearch: true,
    },
    optionsContainer
      ?.items?.slice()
      .sort((a, b) =>
        a._instanceName.toLowerCase().localeCompare(b._instanceName.toLowerCase()),
      )
      .map((entity) =>
        createElement(
          Select.Option,
          {
            value: entity.id,
            key: entity.id,
            title: entity._instanceName,
            style: { whiteSpace: "normal", wordWrap: "break-word" },
          },
          entity._instanceName,
        ),
      ),
  );
});
